import { ChevronDown } from 'lucide-react';
import { useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { cn } from './cn';

export interface DisclosureProps {
  title: ReactNode;
  children: ReactNode;
  description?: string;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  disabled?: boolean;
  variant?: 'card' | 'row' | 'group';
  stickyHeader?: boolean;
  headerWidth?: number;
  size?: 'sm' | 'md';
  contentSpacing?: 'normal' | 'indent' | 'none';
  headingLevel?: 2 | 3 | 4 | 5 | 6;
  region?: boolean;
  className?: string;
}

/** Заголовок содержит только текст/значки; ссылки и действия принадлежат раскрытой области. */
export function Disclosure({
  title,
  children,
  description,
  open: controlled,
  defaultOpen = false,
  onOpenChange,
  disabled = false,
  variant = 'card',
  stickyHeader = false,
  headerWidth,
  size = 'md',
  contentSpacing = 'normal',
  headingLevel = 3,
  region = true,
  className,
}: DisclosureProps) {
  const [localOpen, setLocalOpen] = useState(defaultOpen);
  const open = controlled ?? localOpen;
  const id = useId();
  const trigger = useRef<HTMLButtonElement>(null);
  const content = useRef<HTMLDivElement>(null);
  const heldFocus = useRef(false);
  const Heading = `h${headingLevel}` as 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

  useLayoutEffect(() => {
    if (!open && heldFocus.current) {
      const active = document.activeElement;
      if (active === document.body || content.current?.contains(active)) trigger.current?.focus();
      heldFocus.current = false;
    }
  }, [open]);
  return (
    <div
      data-slot="disclosure"
      data-state={open ? 'open' : 'closed'}
      className={cn(
        'min-w-0',
        variant === 'card' && 'overflow-hidden rounded-lg border border-line bg-card',
        variant === 'group' && 'border-b border-line',
        className,
      )}
    >
      <Heading
        className={cn(
          'min-w-0',
          stickyHeader && 'sticky left-0 z-raised',
          variant === 'group' && 'border-b border-line bg-muted',
        )}
        style={headerWidth === undefined ? undefined : { width: headerWidth }}
      >
        <button
          ref={trigger}
          type="button"
          data-disclosure-trigger=""
          id={id + '-trigger'}
          aria-expanded={open}
          aria-controls={id + '-content'}
          disabled={disabled}
          onClick={() => {
            if (controlled === undefined) setLocalOpen(!open);
            onOpenChange?.(!open);
          }}
          className={cn(
            'flex w-full items-center gap-3 rounded-sm px-4 text-left text-sm font-medium text-fg transition-colors duration-fast hover:bg-hover focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none focus-visible:ring-inset disabled:cursor-not-allowed disabled:opacity-50',
            size === 'sm' ? 'py-2' : 'py-3',
          )}
        >
          <span className="min-w-0 flex-1">
            <span className="block min-w-0 break-words">{title}</span>
            {description && (
              <span className="mt-1 block text-xs font-normal text-fg-muted">{description}</span>
            )}
          </span>
          <ChevronDown
            aria-hidden
            className={cn(
              'size-4 shrink-0 text-fg-muted transition-transform duration-normal ease-standard motion-reduce:transition-none',
              open && 'rotate-180',
            )}
          />
        </button>
      </Heading>
      <div
        ref={content}
        id={id + '-content'}
        data-slot="disclosure-content"
        data-state={open ? 'open' : 'closed'}
        aria-hidden={!open}
        inert={!open}
        role={region ? 'region' : undefined}
        aria-labelledby={region ? id + '-trigger' : undefined}
        onFocusCapture={() => {
          heldFocus.current = true;
        }}
        onBlurCapture={(event) => {
          if (event.relatedTarget && !event.currentTarget.contains(event.relatedTarget))
            heldFocus.current = false;
        }}
      >
        <div className={contentSpacing === 'none' ? 'min-h-0 overflow-clip' : 'min-h-0 overflow-hidden'}>
          <div
            className={cn(
              'text-sm text-fg-muted',
              contentSpacing !== 'none' && 'pt-1 pb-4',
              contentSpacing === 'indent' ? 'pr-4 pl-10' : contentSpacing === 'normal' ? 'px-4' : undefined,
            )}
          >
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}
